import { Pipe, PipeTransform } from '@angular/core';
import { ValidationErrors } from '@angular/forms';

interface MinLengthError {
  requiredLength: number;
  actualLength: number;
}

interface MaxLengthError {
  requiredLength: number;
  actualLength: number;
}

interface MinAmountError {
  requiredValue: number;
  actualValue: number;
}

interface MinError {
  min: number;
  actual: number;
}

interface MaxError {
  max: number;
  actual: number;
}

type DetailedError = MinLengthError | MaxLengthError;

type DetailedAmountError = MinAmountError;

@Pipe({
  name: 'errorKeys',
  standalone: true,
})
export class ErrorMessagesPipe implements PipeTransform {

  transform(errors: ValidationErrors | null, label: string): string[] {
    if (!errors) {
      return [];
    }

    const messages: string[] = [];

    if (errors['required']) {
      messages.push(`${label} is required`);
    }

    if (errors['minlength']) {
      const minLengthError = errors['minlength'] as MinLengthError;
      messages.push(
        `${label} must be at least ${minLengthError.requiredLength} characters long`
      );
    }

    if (errors['maxlength']) {
      const maxLengthError = errors['maxlength'] as MaxLengthError;
      messages.push(
        `${label} cannot be more than ${maxLengthError.requiredLength} characters long`
      );
    }

    if (errors['passwordsDoNotMatch']) {
      messages.push(`Passwords do not match`);
    }

    if (errors['minAmount']) {
      const minAmountError = errors['minAmount'] as MinAmountError;
      if (minAmountError.actualValue > 0) {
        messages.push(
          `${label} amount must be at least $${minAmountError.requiredValue.toLocaleString()}`
        );
      }
    }

    if (errors['min']) {
      const minError = errors['min'] as MinError;
      messages.push(
        `${label} must be at least ${minError.min}`
      );
    }

    if (errors['max']) {
      const maxError = errors['max'] as MaxError;
      messages.push(
        `${label} cannot exceed ${maxError.max}`
      );
    }

    if (errors['invalidFormat']) {
      messages.push(` Date format has to be MM/DD/YYYY`);
    }
    if (errors['futureDateNotAllowed']) {
      messages.push(` Date format has to be MM/DD/YYYY`);
    }
    
    if (errors['invalidPhone']) {
      messages.push('Invalid phone number format. Use format: +CountryCode followed by number (e.g., +919876543210)');
    }
    
    // Handle pattern validation
    if (errors['pattern'] && errors['pattern'].requiredPattern) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const zipPattern = /^[0-9]{6}$/;
      const phonePattern = /^[0-9]{10,13}$/;
      
      if (errors['pattern'].requiredPattern === emailPattern.toString()) {
        messages.push('Please enter a valid email address (e.g., user@example.com)');
      } else if (errors['pattern'].requiredPattern === zipPattern.toString()) {
        messages.push(`${label} must be exactly 6 digits`);
      } else if (errors['pattern'].requiredPattern === phonePattern.toString()) {
        messages.push(`${label} must be between 10 and 13 digits`);
      } else {
        messages.push('Contains invalid characters');
      }
    }
    
    return messages;
  }

  private getError(key: string, errorValue: DetailedError | string | null): string {
    switch (key) {
      case 'required':
        return 'is required';
      case 'minlength':
        return `must be at least ${(errorValue as MinLengthError).requiredLength} characters long`;
      case 'maxlength':
        return `cannot be more than ${(errorValue as MaxLengthError).requiredLength} characters long`;
      case 'pattern':
        return 'contains invalid characters';
      case 'email':
        return 'has Invalid Entry';
      case 'date':
        return 'has Invalid Date';
      case 'minAmount':
        if (errorValue && typeof errorValue === 'object' && 'requiredValue' in errorValue && 'actualValue' in errorValue) {
          const minAmountError = errorValue as MinAmountError;
          return `at least $${minAmountError.requiredValue.toLocaleString()}`;
        }
        return 'has an invalid value';
      default:
        return 'has an error';
    }
  }
}
